import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Badge } from '../../components/ui/badge';
import { Skeleton } from '../../components/ui/skeleton';
import { Button } from '../../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Textarea } from '../../components/ui/textarea';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Checkbox } from '../../components/ui/checkbox';
import { 
  FileText, Plus, Search, Calendar, Building2, Package, Wrench, 
  Eye, X, Upload, Hash, Filter,
  CheckCircle, XCircle, Clock, AlertCircle, DollarSign, FileCheck, Briefcase,
  CreditCard, Trash2, Truck, Pencil, GitCompareArrows
} from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const PurchasingOffers = ({ readOnly = false }) => {
  const { user } = useAuth();
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  
  // Filtre state'leri
  const [filterProduct, setFilterProduct] = useState('');
  const [filterCari, setFilterCari] = useState('');
  const [filterBranch, setFilterBranch] = useState('');
  const [filterDateStart, setFilterDateStart] = useState('');
  const [filterDateEnd, setFilterDateEnd] = useState('');
  
  // Cari hesaplar, stok kartları ve şubeler
  const [cariHesaplar, setCariHesaplar] = useState([]);
  const [stokKartlari, setStokKartlari] = useState([]);
  const [branches, setBranches] = useState([]);
  
  // Modal states
  const [showAddModal, setShowAddModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);
  const [selectedOffer, setSelectedOffer] = useState(null);
  const [isEditing, setIsEditing] = useState(false);
  const [nextOfferNo, setNextOfferNo] = useState('');
  const [jobNames, setJobNames] = useState([]);
  
  // Form state
  const [formData, setFormData] = useState({
    cari_hesap_name: '',
    cari_hesap_id: '',
    is_custom_cari: false,
    custom_cari_name: '',
    branch_id: '',
    branch_name: '',
    offer_type: '',
    is_custom_type: false,
    custom_type_name: '',
    items: [],
    services: [],
    documents: [],
    notes: '',
    job_name: '',
    offer_date: new Date().toISOString().split('T')[0],
    // Para birimi
    currency: 'TL',
    // Nakliye
    nakliye_var: false,
    nakliye_ucreti: '',
    // Ödeme planı
    payment_type: '', // pesin, cek, vadeli
    check_count: 1,
    checks: [], // [{date: '', amount: ''}]
    installment_count: 1,
    installments: [], // [{date: '', amount: ''}]
    payment_plan: [] // [{type: 'cek'|'nakit'|'kredi_karti', date: '', amount: '', description: ''}]
  });
  
  // Para birimi sembolleri
  const currencySymbols = {
    'TL': '₺',
    'USD': '$',
    'EUR': '€',
    'GBP': '£'
  };
  
  // Ürün ekleme state
  const [selectedProduct, setSelectedProduct] = useState('');
  const [productQuantity, setProductQuantity] = useState(1);
  const [productUnit, setProductUnit] = useState('');
  const [productPrice, setProductPrice] = useState('');
  
  // Hizmet ekleme state
  const [serviceName, setServiceName] = useState('');
  const [serviceQuantity, setServiceQuantity] = useState(1);
  const [serviceUnit, setServiceUnit] = useState('adet');
  const [servicePrice, setServicePrice] = useState('');
  
  // Tab state
  const [activeTab, setActiveTab] = useState('teklifler');
  
  // Seçimdeki Teklifler (Selected Offers) state
  const [selectedOffers, setSelectedOffers] = useState([]);
  const [selectedOffersLoading, setSelectedOffersLoading] = useState(false);
  const [selectedOffersSearch, setSelectedOffersSearch] = useState('');
  const [approvedSearch, setApprovedSearch] = useState('');
  const [rejectedSearch, setRejectedSearch] = useState('');
  const [showSelectedDetailModal, setShowSelectedDetailModal] = useState(false);
  const [selectedOfferDetail, setSelectedOfferDetail] = useState(null);

  // Karşılaştırma state
  const [compareOffers, setCompareOffers] = useState([]);
  const [showCompareModal, setShowCompareModal] = useState(false);

  const toggleCompareOffer = (offer) => {
    setCompareOffers(prev => {
      const exists = prev.find(o => o.id === offer.id);
      if (exists) return prev.filter(o => o.id !== offer.id);
      if (prev.length >= 4) return prev;
      return [...prev, offer];
    });
  };

  const isSelectedForCompare = (offerId) => compareOffers.some(o => o.id === offerId);

  const getAuthHeaders = () => ({
    'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
    'Content-Type': 'application/json'
  });

  // Verileri çek
  const fetchData = useCallback(async () => {
    try {
      setLoading(true);
      
      const [offersRes, cariRes, stokRes, branchesRes] = await Promise.all([
        axios.get(`${API}/api/purchasing/offers`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/global/cari-hesaplar`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/global/stok-kartlari`, { headers: getAuthHeaders() }),
        axios.get(`${API}/api/branches`, { headers: getAuthHeaders() })
      ]);
      
      setOffers(offersRes.data);
      setSelectedOffers(offersRes.data.filter(o => o.status === 'secimdeki'));
      setCariHesaplar(cariRes.data);
      setStokKartlari(stokRes.data);
      setBranches(branchesRes.data);
      
    } catch (err) {
      console.error('Veriler yüklenemedi:', err);
      toast.error('Veriler yüklenirken hata oluştu');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Seçimdeki Teklifleri çek
  const fetchSelectedOffers = useCallback(async () => {
    try {
      setSelectedOffersLoading(true);
      const response = await axios.get(`${API}/api/purchasing/offers`, { headers: getAuthHeaders() });
      const allOffers = response.data;
      setSelectedOffers(allOffers.filter(o => o.status === 'secimdeki'));
    } catch (err) {
      console.error('Seçimdeki teklifler yüklenemedi:', err);
      toast.error('Seçimdeki teklifler yüklenirken hata oluştu');
    } finally {
      setSelectedOffersLoading(false);
    }
  }, []);

  // Tab değiştiğinde seçimdeki teklifleri yükle
  useEffect(() => {
    if (activeTab === 'secimdeki') {
      fetchSelectedOffers();
    }
  }, [activeTab, fetchSelectedOffers]);

  // Sonraki teklif numarasını getir
  const fetchNextOfferNo = async () => {
    try {
      const response = await axios.get(`${API}/api/purchasing/offers/next-number`, { headers: getAuthHeaders() });
      setNextOfferNo(response.data.next_offer_no);
    } catch (err) {
      console.error('Teklif numarası alınamadı:', err);
      setNextOfferNo('');
    }
  };

  // Devam eden işlerin adlarını getir (teklif modalı için)
  const fetchJobNames = async () => {
    try {
      const response = await axios.get(`${API}/api/purchasing/jobs/active-names`, { headers: getAuthHeaders() });
      setJobNames(response.data?.names || []);
    } catch (err) {
      setJobNames([]);
    }
  };

  // Modal açıldığında sonraki numarayı ve iş adlarını getir
  useEffect(() => {
    if (showAddModal) {
      fetchJobNames();
      if (!isEditing) fetchNextOfferNo();
    }
  }, [showAddModal, isEditing]);

  // Form reset
  const resetForm = () => {
    setFormData({
      cari_hesap_name: '',
      cari_hesap_id: '',
      is_custom_cari: false,
      custom_cari_name: '',
      branch_id: '',
      branch_name: '',
      offer_type: '',
      is_custom_type: false,
      custom_type_name: '',
      items: [],
      services: [],
      documents: [],
      notes: '',
      job_name: '',
      offer_date: new Date().toISOString().split('T')[0],
      currency: 'TL',
      nakliye_var: false,
      nakliye_ucreti: '',
      payment_type: '',
      check_count: 1,
      checks: [],
      installment_count: 1,
      installments: [],
      payment_plan: []
    });
    setSelectedProduct('');
    setProductQuantity(1);
    setProductUnit('');
    setProductPrice('');
    setServiceName('');
    setServiceQuantity(1);
    setServiceUnit('adet');
    setServicePrice('');
  };

  // Cari hesap seçimi
  const handleCariSelect = (value) => {
    if (value === 'diger') {
      setFormData(prev => ({
        ...prev,
        cari_hesap_id: '',
        cari_hesap_name: '',
        is_custom_cari: true
      }));
    } else {
      const selected = cariHesaplar.find(c => c.id === value);
      setFormData(prev => ({
        ...prev,
        cari_hesap_id: value,
        cari_hesap_name: selected?.name || '',
        is_custom_cari: false,
        custom_cari_name: ''
      }));
    }
  };

  // Teklif tipi seçimi
  const handleTypeSelect = (value) => {
    if (value === 'diger') {
      setFormData(prev => ({
        ...prev,
        offer_type: 'diger',
        is_custom_type: true,
        items: [],
        services: []
      }));
    } else {
      setFormData(prev => ({
        ...prev,
        offer_type: value,
        is_custom_type: false,
        custom_type_name: '',
        items: value === 'urun' ? prev.items : [],
        services: value === 'hizmet' ? prev.services : []
      }));
    }
  };

  // Ürün seçimi değiştiğinde birim otomatik gelsin
  const handleProductSelect = (productId) => {
    setSelectedProduct(productId);
    const product = stokKartlari.find(s => s.id === productId);
    if (product) {
      setProductUnit(product.unit || product.birim || 'adet');
    }
  };

  // Ürün ekle
  const addProduct = () => {
    if (!selectedProduct) {
      toast.error('Lütfen bir ürün seçin');
      return;
    }
    
    const product = stokKartlari.find(s => s.id === selectedProduct);
    if (!product) return;
    
    const newItem = {
      id: selectedProduct,
      product_name: product.name || product.product_name,
      model: product.model || '',
      quantity: productQuantity,
      unit: productUnit || product.unit || product.birim || 'adet',
      price: parseFloat(productPrice) || 0,
      kdv_orani: 20
    };
    
    setFormData(prev => ({
      ...prev,
      items: [...prev.items, newItem]
    }));
    
    setSelectedProduct('');
    setProductQuantity(1);
    setProductUnit('');
    setProductPrice('');
  };

  // Ürün sil
  const removeProduct = (index) => {
    setFormData(prev => ({
      ...prev,
      items: prev.items.filter((_, i) => i !== index)
    }));
  };

  // Hizmet ekle
  const addService = () => {
    if (!serviceName.trim()) {
      toast.error('Lütfen hizmet adı girin');
      return;
    }
    
    const newService = {
      id: Date.now().toString(),
      service_name: serviceName.trim(),
      quantity: serviceQuantity,
      unit: serviceUnit || 'adet',
      price: parseFloat(servicePrice) || 0,
      kdv_orani: 20
    };
    
    setFormData(prev => ({
      ...prev,
      services: [...prev.services, newService]
    }));
    
    setServiceName('');
    setServiceQuantity(1);
    setServiceUnit('adet');
    setServicePrice('');
  };

  // Hizmet sil
  const removeService = (index) => {
    setFormData(prev => ({
      ...prev,
      services: prev.services.filter((_, i) => i !== index)
    }));
  };

  // Belge yükleme
  const handleFileUpload = async (e) => {
    const files = e.target.files;
    if (!files || files.length === 0) return;
    
    const formDataUpload = new FormData();
    for (let i = 0; i < files.length; i++) {
      formDataUpload.append('files', files[i]);
    }
    
    try {
      const response = await axios.post(
        `${API}/api/upload/offers`,
        formDataUpload,
        {
          headers: {
            'Authorization': `Bearer ${localStorage.getItem('fortis_token')}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      const uploadedFiles = response.data.files || [response.data];
      setFormData(prev => ({
        ...prev,
        documents: [...prev.documents, ...uploadedFiles]
      }));
      
      toast.success('Belgeler yüklendi');
    } catch (err) {
      console.error('Belge yükleme hatası:', err);
      toast.error('Belgeler yüklenemedi');
    }
  };

  // Belge sil
  const removeDocument = (index) => {
    setFormData(prev => ({
      ...prev,
      documents: prev.documents.filter((_, i) => i !== index)
    }));
  };

  // Toplam hesapla
  const calculateSubtotal = (items, services) => {
    const itemsTotal = (items || []).reduce((sum, item) => sum + (item.quantity * item.price), 0);
    const servicesTotal = (services || []).reduce((sum, srv) => sum + (srv.quantity * srv.price), 0);
    return itemsTotal + servicesTotal;
  };

  const calculateKdvTotal = (items, services) => {
    const itemsKdv = (items || []).reduce((sum, item) => sum + (item.quantity * item.price * ((item.kdv_orani || 0) / 100)), 0);
    const servicesKdv = (services || []).reduce((sum, srv) => sum + (srv.quantity * srv.price * ((srv.kdv_orani || 0) / 100)), 0);
    return itemsKdv + servicesKdv;
  };

  const calculateTotal = (items, services, nakliye = 0) => {
    return calculateSubtotal(items, services) + calculateKdvTotal(items, services) + (parseFloat(nakliye) || 0);
  };

  // Teklif kaydet
  const handleSaveOffer = async () => {
    // Validasyon
    const cariName = formData.is_custom_cari ? formData.custom_cari_name : formData.cari_hesap_name;
    if (!cariName) {
      toast.error('Lütfen cari hesap seçin veya girin');
      return;
    }
    
    if (!formData.offer_type) {
      toast.error('Lütfen teklif içeriği seçin');
      return;
    }
    
    if (formData.offer_type === 'urun' && formData.items.length === 0) {
      toast.error('Lütfen en az bir ürün ekleyin');
      return;
    }
    
    if (formData.offer_type === 'hizmet' && formData.services.length === 0) {
      toast.error('Lütfen en az bir hizmet ekleyin');
      return;
    }
    
    try {
      const payload = {
        ...formData,
        cari_hesap_name: cariName
      };
      
      if (isEditing && selectedOffer) {
        await axios.put(
          `${API}/api/purchasing/offers/${selectedOffer.id}`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success('Teklif güncellendi');
      } else {
        const response = await axios.post(
          `${API}/api/purchasing/offers`,
          payload,
          { headers: getAuthHeaders() }
        );
        toast.success(`Teklif oluşturuldu: ${response.data.offer_no}`);
      }
      
      setShowAddModal(false);
      resetForm();
      setIsEditing(false);
      fetchData();
      
    } catch (err) {
      console.error('Teklif kaydetme hatası:', err);
      toast.error('Teklif kaydedilemedi');
    }
  };

  // Teklifi görüntüle
  const viewOffer = (offer) => {
    setSelectedOffer(offer);
    setShowViewModal(true);
  };

  // Teklifi düzenle
  const editOffer = (offer) => {
    setFormData({
      cari_hesap_name: offer.cari_hesap_name || '',
      cari_hesap_id: offer.cari_hesap_id || '',
      is_custom_cari: offer.is_custom_cari || false,
      custom_cari_name: offer.is_custom_cari ? offer.cari_hesap_name : '',
      branch_id: offer.branch_id || '',
      branch_name: offer.branch_name || '',
      offer_type: offer.offer_type || '',
      is_custom_type: offer.is_custom_type || false,
      custom_type_name: offer.custom_type_name || '',
      items: offer.items || [],
      services: offer.services || [],
      documents: offer.documents || [],
      notes: offer.notes || '',
      job_name: offer.job_name || '',
      offer_date: offer.offer_date || new Date().toISOString().split('T')[0],
      currency: offer.currency || 'TL',
      nakliye_var: offer.nakliye_var || false,
      nakliye_ucreti: offer.nakliye_ucreti || '',
      payment_type: offer.payment_type || '',
      check_count: offer.check_count || 1,
      checks: offer.checks || [],
      installment_count: offer.installment_count || 1,
      installments: offer.installments || [],
      payment_plan: offer.payment_plan || []
    });
    setSelectedOffer(offer);
    setIsEditing(true);
    setShowAddModal(true);
  };

  // Teklifi sil
  const deleteOffer = async (offerId) => {
    if (!window.confirm('Bu teklifi silmek istediğinizden emin misiniz?')) return;
    
    try {
      await axios.delete(`${API}/api/purchasing/offers/${offerId}`, { headers: getAuthHeaders() });
      toast.success('Teklif silindi');
      fetchData();
    } catch (err) {
      toast.error('Teklif silinemedi');
    }
  };

  // Durum güncelle
  const updateStatus = async (offerId, newStatus) => {
    try {
      await axios.put(
        `${API}/api/purchasing/offers/${offerId}/status`,
        { status: newStatus },
        { headers: getAuthHeaders() }
      );
      toast.success('Durum güncellendi');
      fetchData();
      setShowViewModal(false);
    } catch (err) {
      toast.error('Durum güncellenemedi');
    }
  };

  // Tarih formatla
  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('tr-TR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  // Para formatla
  const formatMoney = (amount) => {
    return new Intl.NumberFormat('tr-TR', {
      style: 'currency',
      currency: 'TRY'
    }).format(amount || 0);
  };

  // Durum badge
  const getStatusBadge = (status) => {
    switch (status) {
      case 'aktif':
        return <Badge className="bg-blue-100 text-blue-700"><Clock className="h-3 w-3 mr-1" />Aktif</Badge>;
      case 'kabul_edildi':
        return <Badge className="bg-green-100 text-green-700"><CheckCircle className="h-3 w-3 mr-1" />Kabul Edildi</Badge>;
      case 'reddedildi':
        return <Badge className="bg-red-100 text-red-700"><XCircle className="h-3 w-3 mr-1" />Reddedildi</Badge>;
      case 'iptal':
        return <Badge className="bg-gray-100 text-gray-700"><AlertCircle className="h-3 w-3 mr-1" />İptal</Badge>;
      default:
        return <Badge>{status}</Badge>;
    }
  };

  // Tip badge
  const getTypeBadge = (offer) => {
    if (offer.is_custom_type) {
      return <Badge variant="outline">{offer.custom_type_name}</Badge>;
    }
    if (offer.offer_type === 'urun') {
      return <Badge className="bg-purple-100 text-purple-700"><Package className="h-3 w-3 mr-1" />Ürün</Badge>;
    }
    if (offer.offer_type === 'hizmet') {
      return <Badge className="bg-amber-100 text-amber-700"><Wrench className="h-3 w-3 mr-1" />Hizmet</Badge>;
    }
    return <Badge variant="outline">{offer.offer_type}</Badge>;
  };

  // Arama ve filtreleme
  // Teklifin tüm detaylarını içeren aranabilir metin (derin arama için)
  const buildOfferSearchText = (o) => {
    const parts = [
      o.offer_no, o.cari_hesap_name, o.offer_type, o.custom_type_name,
      o.notes, o.description, o.branch_name, o.offer_date, o.status,
      o.created_by_name, o.payment_type, o.currency
    ];
    (o.items || []).forEach(item => {
      parts.push(item.product_name, item.model, item.unit, item.quantity, item.price);
    });
    (o.services || []).forEach(srv => {
      parts.push(srv.service_name, srv.unit, srv.quantity, srv.price);
    });
    (o.documents || []).forEach(doc => parts.push(doc.name || doc.filename));
    (o.checks || []).forEach(chk => parts.push(chk.bank, chk.date, chk.amount));
    (o.installments || []).forEach(inst => parts.push(inst.date, inst.amount));
    return parts.filter(Boolean).join(' ').toLowerCase();
  };

  const filteredOffers = offers.filter(o => {
    // Metin araması - tüm detaylarda kelime bazlı (çoklu kelime = AND)
    const searchText = buildOfferSearchText(o);
    const tokens = search.trim().toLowerCase().split(/\s+/).filter(Boolean);
    const matchesSearch = tokens.length === 0 || tokens.every(t => searchText.includes(t));    
    // Ürün filtresi
    const matchesProduct = filterProduct === '' || filterProduct === 'all' ||
      (o.items || []).some(item => item.product_name === filterProduct);
    
    // Cari hesap filtresi
    const matchesCari = filterCari === '' || filterCari === 'all' ||
      o.cari_hesap_name === filterCari;
    
    // Şube filtresi
    const matchesBranch = filterBranch === '' || filterBranch === 'all' ||
      o.branch_id === filterBranch;
    
    // Tarih filtresi
    const offerDate = o.offer_date ? new Date(o.offer_date) : null;
    const startDate = filterDateStart ? new Date(filterDateStart) : null;
    const endDate = filterDateEnd ? new Date(filterDateEnd) : null;
    
    const matchesDateStart = !startDate || (offerDate && offerDate >= startDate);
    const matchesDateEnd = !endDate || (offerDate && offerDate <= endDate);
    
    return matchesSearch && matchesProduct && matchesCari && matchesBranch && matchesDateStart && matchesDateEnd && o.status === 'aktif';
  });

  // Derin arama yardımcısı (çoklu kelime = AND)
  const matchOfferSearch = (o, term) => {
    const tokens = (term || '').trim().toLowerCase().split(/\s+/).filter(Boolean);
    if (tokens.length === 0) return true;
    const txt = buildOfferSearchText(o);
    return tokens.every(t => txt.includes(t));
  };
  const approvedOffers = offers.filter(o => o.status === 'kabul_edildi' && matchOfferSearch(o, approvedSearch));
  const rejectedOffers = offers.filter(o => o.status === 'reddedildi' && matchOfferSearch(o, rejectedSearch));

  // Benzersiz ürün, cari hesap ve şube listesi (filtre seçenekleri için)
  const uniqueProducts = [...new Set(
    offers.flatMap(o => (o.items || []).map(item => item.product_name)).filter(Boolean)
  )].sort();
  
  const uniqueCariHesaplar = [...new Set(
    offers.map(o => o.cari_hesap_name).filter(Boolean)
  )].sort();
  
  const uniqueBranches = [...new Set(
    offers.map(o => o.branch_id).filter(Boolean)
  )];

  // Filtreleri temizle
  const clearFilters = () => {
    setFilterProduct('');
    setFilterCari('');
    setFilterBranch('');
    setFilterDateStart('');
    setFilterDateEnd('');
    setSearch('');
  };

  if (loading) {
    return (
      <div className="space-y-6" data-testid="offers-loading">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Teklifler</h1>
            <p className="text-gray-500">Teklifler ve teklif yönetimi</p>
          </div>
        </div>
        <Card>
          <CardContent className="py-6">
            <div className="space-y-3">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="purchasing-offers">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Teklifler</h1>
          <p className="text-gray-500">Teklif yönetimi ve seçimdeki teklifler</p>
        </div>
        {activeTab === 'teklifler' && (
          <Button 
            onClick={() => { resetForm(); setIsEditing(false); setShowAddModal(true); }}
            className="bg-orange-600 hover:bg-orange-700"
            data-testid="add-offer-btn"
          >
            <Plus className="h-4 w-4 mr-2" />
            Teklif Ekle
          </Button>
        )}
      </div>

      {/* Sekmeler */}
      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="grid w-full grid-cols-4 mb-6">
          <TabsTrigger value="teklifler" className="flex items-center gap-2" data-testid="tab-teklifler">
            <FileText className="h-4 w-4" />
            Teklif Listesi
            {offers.filter(o => o.status === 'aktif').length > 0 && (
              <Badge variant="secondary" className="ml-1">{offers.filter(o => o.status === 'aktif').length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="onaylanan" className="flex items-center gap-2" data-testid="tab-onaylanan">
            <CheckCircle className="h-4 w-4" />
            Onaylanan
            {offers.filter(o => o.status === 'kabul_edildi').length > 0 && (
              <Badge variant="secondary" className="ml-1 bg-green-100 text-green-700">{offers.filter(o => o.status === 'kabul_edildi').length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="reddedilen" className="flex items-center gap-2" data-testid="tab-reddedilen">
            <XCircle className="h-4 w-4" />
            Red Edilen
            {offers.filter(o => o.status === 'reddedildi').length > 0 && (
              <Badge variant="secondary" className="ml-1 bg-red-100 text-red-700">{offers.filter(o => o.status === 'reddedildi').length}</Badge>
            )}
          </TabsTrigger>
          <TabsTrigger value="secimdeki" className="flex items-center gap-2" data-testid="tab-secimdeki-teklifler">
            <FileCheck className="h-4 w-4" />
            Seçimdeki Teklifler
            {selectedOffers.length > 0 && (
              <Badge variant="secondary" className="ml-1">{selectedOffers.length}</Badge>
            )}
          </TabsTrigger>
        </TabsList>

        {/* Karşılaştır Butonu */}
        <div className="flex items-center justify-between mb-4">
          <div className="flex items-center gap-2">
            {compareOffers.length > 0 && (
              <div className="flex items-center gap-2 text-sm text-gray-600">
                <span>{compareOffers.length}/4 teklif seçili:</span>
                {compareOffers.map(o => (
                  <Badge key={o.id} variant="outline" className="gap-1 bg-blue-50 text-blue-700 border-blue-200">
                    {o.offer_no}
                    <X className="h-3 w-3 cursor-pointer" onClick={() => toggleCompareOffer(o)} />
                  </Badge>
                ))}
              </div>
            )}
          </div>
          <Button
            variant={compareOffers.length >= 2 ? "default" : "outline"}
            size="sm"
            disabled={compareOffers.length < 2}
            onClick={() => setShowCompareModal(true)}
            className={compareOffers.length >= 2 ? "bg-indigo-600 hover:bg-indigo-700 gap-2" : "gap-2"}
            data-testid="compare-offers-btn"
          >
            <GitCompareArrows className="h-4 w-4" />
            Karşılaştır {compareOffers.length >= 2 && `(${compareOffers.length})`}
          </Button>
        </div>

        {/* Teklifler Tab Content */}
        <TabsContent value="teklifler" className="space-y-6">
          {/* İstatistikler */}
      <div className="grid grid-cols-4 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100">
                <FileText className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Toplam Teklif</p>
                <p className="text-2xl font-bold">{offers.length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-blue-100">
                <Clock className="h-6 w-6 text-blue-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Aktif</p>
                <p className="text-2xl font-bold text-blue-600">{offers.filter(o => o.status === 'aktif').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-green-100">
                <CheckCircle className="h-6 w-6 text-green-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Kabul Edilen</p>
                <p className="text-2xl font-bold text-green-600">{offers.filter(o => o.status === 'kabul_edildi').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-full bg-red-100">
                <XCircle className="h-6 w-6 text-red-600" />
              </div>
              <div>
                <p className="text-sm text-gray-500">Reddedilen</p>
                <p className="text-2xl font-bold text-red-600">{offers.filter(o => o.status === 'reddedildi').length}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Teklif Listesi */}
      <Card>
        <CardHeader className="flex flex-col gap-4">
          <div className="flex flex-row items-center justify-between">
            <CardTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-600" />
              Teklif Listesi
            </CardTitle>
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
              <Input
                placeholder="Teklif no, firma, ürün, not... ara"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="pl-10 w-72"
                data-testid="offer-search-input"
              />
            </div>
          </div>
          
          {/* Filtreler */}
          <div className="flex flex-wrap items-end gap-4 p-4 bg-gray-50 rounded-lg border">
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs text-gray-500 mb-1 block">Ürün</Label>
              <Select value={filterProduct} onValueChange={setFilterProduct}>
                <SelectTrigger data-testid="filter-product">
                  <SelectValue placeholder="Tüm ürünler" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm ürünler</SelectItem>
                  {uniqueProducts.map((product) => (
                    <SelectItem key={product} value={product}>
                      {product}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs text-gray-500 mb-1 block">Cari Hesap</Label>
              <Select value={filterCari} onValueChange={setFilterCari}>
                <SelectTrigger data-testid="filter-cari">
                  <SelectValue placeholder="Tüm cari hesaplar" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm cari hesaplar</SelectItem>
                  {uniqueCariHesaplar.map((cari) => (
                    <SelectItem key={cari} value={cari}>
                      {cari}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-[180px]">
              <Label className="text-xs text-gray-500 mb-1 block">Şube</Label>
              <Select value={filterBranch} onValueChange={setFilterBranch}>
                <SelectTrigger data-testid="filter-branch">
                  <SelectValue placeholder="Tüm şubeler" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tüm şubeler</SelectItem>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs text-gray-500 mb-1 block">Başlangıç Tarihi</Label>
              <Input
                type="date"
                value={filterDateStart}
                onChange={(e) => setFilterDateStart(e.target.value)}
                data-testid="filter-date-start"
              />
            </div>
            
            <div className="flex-1 min-w-[150px]">
              <Label className="text-xs text-gray-500 mb-1 block">Bitiş Tarihi</Label>
              <Input
                type="date"
                value={filterDateEnd}
                onChange={(e) => setFilterDateEnd(e.target.value)}
                data-testid="filter-date-end"
              />
            </div>
            
            <Button 
              variant="outline" 
              size="sm"
              onClick={clearFilters}
              className="h-10"
              data-testid="clear-filters-btn"
            >
              <X className="h-4 w-4 mr-1" />
              Temizle
            </Button>
          </div>
          
          {/* Filtre sonucu bilgisi */}
          {(filterProduct || filterCari || filterDateStart || filterDateEnd || search) && (
            <div className="text-sm text-gray-500">
              {filteredOffers.length} / {offers.length} teklif gösteriliyor
            </div>
          )}
        </CardHeader>
        <CardContent>
          {filteredOffers.length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-gray-500">
              <FileText className="h-12 w-12 mb-4 text-gray-300" />
              <p>Henüz teklif bulunmuyor</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => { resetForm(); setIsEditing(false); setShowAddModal(true); }}
              >
                <Plus className="h-4 w-4 mr-2" />
                İlk Teklifi Ekle
              </Button>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-10"></TableHead>
                  <TableHead>Teklif No</TableHead>
                  <TableHead>İş Adı</TableHead>
                  <TableHead>Tarih</TableHead>
                  <TableHead>Şube</TableHead>
                  <TableHead>Cari Hesap</TableHead>
                  <TableHead>Tür</TableHead>
                  <TableHead>İçerik</TableHead>
                  <TableHead className="text-right">Toplam</TableHead>
                  <TableHead>Durum</TableHead>
                  <TableHead className="text-right">Görüntüle</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {filteredOffers.map((offer) => (
                  <TableRow key={offer.id} data-testid={`offer-row-${offer.id}`} className={isSelectedForCompare(offer.id) ? 'bg-indigo-50' : ''}>
                    <TableCell>
                      <Checkbox
                        checked={isSelectedForCompare(offer.id)}
                        onCheckedChange={() => toggleCompareOffer(offer)}
                        disabled={!isSelectedForCompare(offer.id) && compareOffers.length >= 4}
                        data-testid={`compare-check-${offer.id}`}
                      />
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 font-mono font-semibold text-orange-600">
                        <Hash className="h-4 w-4" />
                        {offer.offer_no || '-'}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2 max-w-[180px]" data-testid={`offer-job-name-${offer.id}`}>
                        <Briefcase className="h-4 w-4 text-gray-400 shrink-0" />
                        <span className="truncate">{offer.job_name || '-'}</span>
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Calendar className="h-4 w-4 text-gray-400" />
                        {formatDate(offer.offer_date)}
                      </div>
                    </TableCell>
                    <TableCell>
                      <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                        {offer.branch_name || 'Belirtilmemiş'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Building2 className="h-4 w-4 text-gray-400" />
                        {offer.cari_hesap_name}
                      </div>
                    </TableCell>
                    <TableCell>{getTypeBadge(offer)}</TableCell>
                    <TableCell className="max-w-[200px]">
                      {offer.offer_type === 'urun' ? (
                        <div className="text-sm">
                          {(offer.items || []).slice(0, 2).map((item, idx) => (
                            <div key={idx} className="truncate">
                              • {item.product_name} ({item.quantity} {item.unit})
                            </div>
                          ))}
                          {(offer.items || []).length > 2 && (
                            <div className="text-gray-500">+{offer.items.length - 2} ürün daha</div>
                          )}
                        </div>
                      ) : offer.offer_type === 'hizmet' ? (
                        <div className="text-sm">
                          {(offer.services || []).slice(0, 2).map((srv, idx) => (
                            <div key={idx} className="truncate">
                              • {srv.service_name} ({srv.quantity} {srv.unit})
                            </div>
                          ))}
                          {(offer.services || []).length > 2 && (
                            <div className="text-gray-500">+{offer.services.length - 2} hizmet daha</div>
                          )}
                        </div>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-right font-mono font-semibold">
                      {formatMoney(calculateTotal(offer.items, offer.services, offer.nakliye_var ? offer.nakliye_ucreti : 0))}
                    </TableCell>
                    <TableCell>{getStatusBadge(offer.status)}</TableCell>
                    <TableCell className="text-right">
                      <div className="flex items-center justify-end gap-1">
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => editOffer(offer)}
                          data-testid={`edit-offer-${offer.id}`}
                          title="Düzenle"
                        >
                          <Pencil className="h-4 w-4 text-blue-600" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="sm"
                          onClick={() => viewOffer(offer)}
                          data-testid={`view-offer-${offer.id}`}
                          title="Detayları Görüntüle"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>
        </TabsContent>


        {/* Onaylanan Teklifler Tab Content */}
        <TabsContent value="onaylanan" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <CheckCircle className="h-5 w-5 text-green-600" />
                Onaylanan Teklifler
              </CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Teklif no, firma, ürün, not... ara"
                  value={approvedSearch}
                  onChange={(e) => setApprovedSearch(e.target.value)}
                  className="pl-10 w-72"
                  data-testid="approved-offer-search-input"
                />
              </div>
            </CardHeader>
            <CardContent>
              {approvedOffers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <CheckCircle className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">{approvedSearch ? 'Eşleşen teklif bulunamadı' : 'Onaylanan teklif bulunmuyor'}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Teklif No</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Şube</TableHead>
                      <TableHead>Cari Hesap</TableHead>
                      <TableHead>İçerik</TableHead>
                      <TableHead className="text-right">Toplam</TableHead>
                      <TableHead>İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {approvedOffers.map(offer => (
                      <TableRow key={offer.id} data-testid={`approved-offer-${offer.id}`} className={isSelectedForCompare(offer.id) ? 'bg-indigo-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={isSelectedForCompare(offer.id)}
                            onCheckedChange={() => toggleCompareOffer(offer)}
                            disabled={!isSelectedForCompare(offer.id) && compareOffers.length >= 4}
                            data-testid={`compare-check-approved-${offer.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{offer.offer_no}</TableCell>
                        <TableCell>{new Date(offer.offer_date || offer.created_at).toLocaleDateString('tr-TR')}</TableCell>
                        <TableCell>{branches.find(b => b.id === offer.branch_id)?.name || '-'}</TableCell>
                        <TableCell>{offer.cari_hesap_name || '-'}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {(offer.items || []).map(item => `${item.product_name} (${item.quantity} ${item.unit})`).join(', ') || '-'}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-green-600">
                          {formatMoney(calculateTotal(offer.items, offer.services, offer.nakliye_var ? offer.nakliye_ucreti : 0))}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setSelectedOffer(offer); setShowViewModal(true); }}
                            data-testid={`view-approved-offer-${offer.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Red Edilen Teklifler Tab Content */}
        <TabsContent value="reddedilen" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <XCircle className="h-5 w-5 text-red-600" />
                Red Edilen Teklifler
              </CardTitle>
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                <Input
                  placeholder="Teklif no, firma, ürün, not... ara"
                  value={rejectedSearch}
                  onChange={(e) => setRejectedSearch(e.target.value)}
                  className="pl-10 w-72"
                  data-testid="rejected-offer-search-input"
                />
              </div>
            </CardHeader>
            <CardContent>
              {rejectedOffers.length === 0 ? (
                <div className="text-center py-12 text-gray-500">
                  <XCircle className="h-16 w-16 mx-auto mb-4 opacity-30" />
                  <p className="text-lg font-medium">{rejectedSearch ? 'Eşleşen teklif bulunamadı' : 'Red edilen teklif bulunmuyor'}</p>
                </div>
              ) : (
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-10"></TableHead>
                      <TableHead>Teklif No</TableHead>
                      <TableHead>Tarih</TableHead>
                      <TableHead>Şube</TableHead>
                      <TableHead>Cari Hesap</TableHead>
                      <TableHead>İçerik</TableHead>
                      <TableHead className="text-right">Toplam</TableHead>
                      <TableHead>İşlemler</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {rejectedOffers.map(offer => (
                      <TableRow key={offer.id} data-testid={`rejected-offer-${offer.id}`} className={isSelectedForCompare(offer.id) ? 'bg-indigo-50' : ''}>
                        <TableCell>
                          <Checkbox
                            checked={isSelectedForCompare(offer.id)}
                            onCheckedChange={() => toggleCompareOffer(offer)}
                            disabled={!isSelectedForCompare(offer.id) && compareOffers.length >= 4}
                            data-testid={`compare-check-rejected-${offer.id}`}
                          />
                        </TableCell>
                        <TableCell className="font-medium">{offer.offer_no}</TableCell>
                        <TableCell>{new Date(offer.offer_date || offer.created_at).toLocaleDateString('tr-TR')}</TableCell>
                        <TableCell>{branches.find(b => b.id === offer.branch_id)?.name || '-'}</TableCell>
                        <TableCell>{offer.cari_hesap_name || '-'}</TableCell>
                        <TableCell>
                          <div className="text-sm">
                            {(offer.items || []).map(item => `${item.product_name} (${item.quantity} ${item.unit})`).join(', ') || '-'}
                          </div>
                        </TableCell>
                        <TableCell className="text-right font-semibold text-red-600">
                          {formatMoney(calculateTotal(offer.items, offer.services, offer.nakliye_var ? offer.nakliye_ucreti : 0))}
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => { setSelectedOffer(offer); setShowViewModal(true); }}
                            data-testid={`view-rejected-offer-${offer.id}`}
                          >
                            <Eye className="h-4 w-4" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Seçimdeki Teklifler Tab Content */}
        <TabsContent value="secimdeki" className="space-y-6">
          <Card>
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="flex items-center gap-2">
                <FileCheck className="h-5 w-5 text-green-600" />
                Seçimdeki Teklifler
              </CardTitle>
              <div className="flex items-center gap-2">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-gray-400" />
                  <Input
                    placeholder="Teklif no, firma, ürün, not... ara"
                    value={selectedOffersSearch}
                    onChange={(e) => setSelectedOffersSearch(e.target.value)}
                    className="pl-10 w-72"
                    data-testid="selected-offer-search-input"
                  />
                </div>
                <Button 
                  variant="outline" 
                  size="sm"
                  onClick={fetchSelectedOffers}
                  data-testid="refresh-selected-offers-btn"
                >
                  <Clock className="h-4 w-4 mr-1" />
                  Yenile
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              {selectedOffersLoading ? (
                <div className="space-y-3">
                  {[1, 2, 3].map((i) => (
                    <Skeleton key={i} className="h-12 w-full" />
                  ))}
                </div>
              ) : (() => {
                const filtered = selectedOffers.filter(o => matchOfferSearch(o, selectedOffersSearch));
                return filtered.length === 0 ? (
                  <div className="flex flex-col items-center justify-center py-12 text-gray-500">
                    <FileCheck className="h-12 w-12 mb-4 text-gray-300" />
                    <p>Seçimdeki teklif bulunmuyor</p>
                  </div>
                ) : (
                  <Table>
                    <TableHeader>
                      <TableRow>
                        <TableHead className="w-10"></TableHead>
                        <TableHead>Teklif No</TableHead>
                        <TableHead>Tarih</TableHead>
                        <TableHead>Cari Hesap</TableHead>
                        <TableHead>Şube</TableHead>
                        <TableHead>Tür</TableHead>
                        <TableHead>İçerik</TableHead>
                        <TableHead className="text-right">Toplam</TableHead>
                        <TableHead className="text-right">İşlem</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {filtered.map((offer) => {
                        const totalPrice = calculateTotal(offer.items, offer.services, offer.nakliye_var ? offer.nakliye_ucreti : 0);
                        const cs = { 'TL': '₺', 'USD': '$', 'EUR': '€', 'GBP': '£' };
                        const symbol = cs[offer.currency] || '₺';
                        
                        return (
                          <TableRow key={offer.id} data-testid={`secimdeki-offer-row-${offer.id}`} className={isSelectedForCompare(offer.id) ? 'bg-indigo-50' : ''}>
                            <TableCell>
                              <Checkbox
                                checked={isSelectedForCompare(offer.id)}
                                onCheckedChange={() => toggleCompareOffer(offer)}
                                disabled={!isSelectedForCompare(offer.id) && compareOffers.length >= 4}
                                data-testid={`compare-check-secimdeki-${offer.id}`}
                              />
                            </TableCell>
                            <TableCell>
                              <span className="font-mono font-semibold text-orange-600"># {offer.offer_no}</span>
                            </TableCell>
                            <TableCell>
                              <div className="flex items-center gap-1 text-sm text-gray-500">
                                <Calendar className="h-3 w-3" />
                                {formatDate(offer.offer_date)}
                              </div>
                            </TableCell>
                            <TableCell className="font-medium">{offer.cari_hesap_name || '-'}</TableCell>
                            <TableCell>
                              {offer.branch_name ? (
                                <Badge variant="outline" className="bg-blue-50 text-blue-700 border-blue-200">
                                  {offer.branch_name}
                                </Badge>
                              ) : '-'}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={offer.offer_type === 'urun' ? 'bg-green-50 text-green-700' : 'bg-purple-50 text-purple-700'}>
                                {offer.offer_type === 'urun' ? 'Ürün' : offer.offer_type === 'hizmet' ? 'Hizmet' : offer.offer_type || '-'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-sm text-gray-600 max-w-[200px]">
                              <div className="truncate">
                                {offer.offer_type === 'urun' 
                                  ? (offer.items || []).map(i => `${i.product_name} (${i.quantity} ${i.unit})`).join(', ')
                                  : (offer.services || []).map(s => `${s.service_name} (${s.quantity} ${s.unit})`).join(', ')
                                }
                              </div>
                            </TableCell>
                            <TableCell className="text-right font-mono font-semibold text-green-600">
                              {totalPrice.toLocaleString('tr-TR')} {symbol}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => {
                                  setSelectedOffer(offer);
                                  setShowViewModal(true);
                                }}
                                data-testid={`view-secimdeki-offer-${offer.id}`}
                                title="Detayları Görüntüle"
                              >
                                <Eye className="h-4 w-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Karşılaştırma Modalı */}
      <Dialog open={showCompareModal} onOpenChange={setShowCompareModal}>
        <DialogContent className="max-w-[95vw] max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <GitCompareArrows className="h-5 w-5 text-indigo-600" />
              Teklif Karşılaştırma ({compareOffers.length} Teklif)
            </DialogTitle>
          </DialogHeader>

          {compareOffers.length >= 2 && (() => {
            // Tüm ürün adlarını topla
            const allProductNames = [];
            compareOffers.forEach(offer => {
              (offer.items || []).forEach(item => {
                const name = item.product_name;
                if (!allProductNames.includes(name)) allProductNames.push(name);
              });
            });
            // Tüm hizmet adlarını topla
            const allServiceNames = [];
            compareOffers.forEach(offer => {
              (offer.services || []).forEach(srv => {
                const name = srv.service_name;
                if (!allServiceNames.includes(name)) allServiceNames.push(name);
              });
            });

            const cs = { 'TL': '₺', 'USD': '$', 'EUR': '€', 'GBP': '£' };

            return (
              <div className="space-y-6">
                {/* Genel Bilgiler */}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-40 bg-gray-50 font-semibold">Bilgi</TableHead>
                      {compareOffers.map(o => (
                        <TableHead key={o.id} className="text-center bg-gray-50">
                          <div className="font-semibold text-orange-600">{o.offer_no}</div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium bg-gray-50">Cari Hesap</TableCell>
                      {compareOffers.map(o => (
                        <TableCell key={o.id} className="text-center">{o.cari_hesap_name || '-'}</TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium bg-gray-50">Tarih</TableCell>
                      {compareOffers.map(o => (
                        <TableCell key={o.id} className="text-center">{formatDate(o.offer_date)}</TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium bg-gray-50">Durum</TableCell>
                      {compareOffers.map(o => (
                        <TableCell key={o.id} className="text-center">{getStatusBadge(o.status)}</TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium bg-gray-50">Para Birimi</TableCell>
                      {compareOffers.map(o => (
                        <TableCell key={o.id} className="text-center">{o.currency || 'TL'}</TableCell>
                      ))}
                    </TableRow>
                  </TableBody>
                </Table>

                {/* Ürün Karşılaştırması */}
                {allProductNames.length > 0 && (
                  <div>
                    <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
                      <Package className="h-4 w-4 text-purple-500" />
                      Ürün Karşılaştırması
                    </h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-40 bg-purple-50 font-semibold">Ürün</TableHead>
                          {compareOffers.map(o => (
                            <TableHead key={o.id} className="text-center bg-purple-50" colSpan={1}>
                              <div className="text-orange-600 font-semibold">{o.offer_no}</div>
                              <div className="text-xs text-gray-500">{o.cari_hesap_name}</div>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allProductNames.map(productName => {
                          const items = compareOffers.map(offer => 
                            (offer.items || []).find(i => i.product_name === productName)
                          );
                          // En düşük fiyatlı teklifi bul
                          const prices = items.map((item, idx) => item ? item.price : Infinity);
                          const minPrice = Math.min(...prices.filter(p => p !== Infinity));

                          return (
                            <TableRow key={productName}>
                              <TableCell className="font-medium bg-gray-50">
                                <div>{productName}</div>
                                {items.find(i => i?.model) && (
                                  <div className="text-xs text-gray-400">Model: {items.find(i => i?.model)?.model}</div>
                                )}
                              </TableCell>
                              {items.map((item, idx) => (
                                <TableCell key={compareOffers[idx].id} className="text-center">
                                  {item ? (
                                    <div className="space-y-1">
                                      <div className="text-sm">{item.quantity} {item.unit}</div>
                                      <div className={`font-semibold ${item.price === minPrice && prices.filter(p => p !== Infinity).length > 1 ? 'text-green-600' : ''}`}>
                                        {(cs[compareOffers[idx].currency] || '₺')}{item.price.toLocaleString('tr-TR')}
                                        {item.price === minPrice && prices.filter(p => p !== Infinity).length > 1 && ' ✓'}
                                      </div>
                                      {item.kdv_orani > 0 && (
                                        <div className="text-xs text-gray-400">KDV %{item.kdv_orani}</div>
                                      )}
                                      <div className="text-xs font-medium text-gray-600">
                                        Toplam: {(cs[compareOffers[idx].currency] || '₺')}{(item.quantity * item.price * (1 + (item.kdv_orani || 0) / 100)).toLocaleString('tr-TR')}
                                      </div>
                                    </div>
                                  ) : (
                                    <span className="text-gray-300">—</span>
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Hizmet Karşılaştırması */}
                {allServiceNames.length > 0 && (
                  <div>
                    <h3 className="text-base font-semibold mb-2 flex items-center gap-2">
                      <Wrench className="h-4 w-4 text-amber-500" />
                      Hizmet Karşılaştırması
                    </h3>
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead className="w-40 bg-amber-50 font-semibold">Hizmet</TableHead>
                          {compareOffers.map(o => (
                            <TableHead key={o.id} className="text-center bg-amber-50">
                              <div className="text-orange-600 font-semibold">{o.offer_no}</div>
                              <div className="text-xs text-gray-500">{o.cari_hesap_name}</div>
                            </TableHead>
                          ))}
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {allServiceNames.map(serviceName => {
                          const services = compareOffers.map(offer =>
                            (offer.services || []).find(s => s.service_name === serviceName)
                          );
                          const prices = services.map((srv, idx) => srv ? srv.price : Infinity);
                          const minPrice = Math.min(...prices.filter(p => p !== Infinity));

                          return (
                            <TableRow key={serviceName}>
                              <TableCell className="font-medium bg-gray-50">{serviceName}</TableCell>
                              {services.map((srv, idx) => (
                                <TableCell key={compareOffers[idx].id} className="text-center">
                                  {srv ? (
                                    <div className="space-y-1">
                                      <div className="text-sm">{srv.quantity} {srv.unit}</div>
                                      <div className={`font-semibold ${srv.price === minPrice && prices.filter(p => p !== Infinity).length > 1 ? 'text-green-600' : ''}`}>
                                        {(cs[compareOffers[idx].currency] || '₺')}{srv.price.toLocaleString('tr-TR')}
                                        {srv.price === minPrice && prices.filter(p => p !== Infinity).length > 1 && ' ✓'}
                                      </div>
                                      {srv.kdv_orani > 0 && (
                                        <div className="text-xs text-gray-400">KDV %{srv.kdv_orani}</div>
                                      )}
                                    </div>
                                  ) : (
                                    <span className="text-gray-300">—</span>
                                  )}
                                </TableCell>
                              ))}
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </div>
                )}

                {/* Toplam Karşılaştırması */}
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="w-40 bg-green-50 font-semibold">Toplam</TableHead>
                      {compareOffers.map(o => (
                        <TableHead key={o.id} className="text-center bg-green-50">
                          <div className="text-orange-600 font-semibold">{o.offer_no}</div>
                        </TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    <TableRow>
                      <TableCell className="font-medium bg-gray-50">Ara Toplam</TableCell>
                      {compareOffers.map(o => (
                        <TableCell key={o.id} className="text-center">
                          {formatMoney(calculateSubtotal(o.items, o.services))}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium bg-gray-50">KDV Toplamı</TableCell>
                      {compareOffers.map(o => (
                        <TableCell key={o.id} className="text-center">
                          {formatMoney(calculateKdvTotal(o.items, o.services))}
                        </TableCell>
                      ))}
                    </TableRow>
                    <TableRow>
                      <TableCell className="font-medium bg-gray-50">Nakliye</TableCell>
                      {compareOffers.map(o => (
                        <TableCell key={o.id} className="text-center">
                          {o.nakliye_var ? formatMoney(parseFloat(o.nakliye_ucreti) || 0) : <span className="text-gray-300">—</span>}
                        </TableCell>
                      ))}
                    </TableRow>
                    {(() => {
                      const totals = compareOffers.map(o => calculateTotal(o.items, o.services, o.nakliye_var ? o.nakliye_ucreti : 0));
                      const minTotal = Math.min(...totals);
                      return (
                        <TableRow className="bg-gray-50 font-bold">
                          <TableCell className="font-bold bg-gray-100">Genel Toplam</TableCell>
                          {compareOffers.map((o, idx) => (
                            <TableCell key={o.id} className={`text-center text-lg ${totals[idx] === minTotal ? 'text-green-600' : ''}`}>
                              {formatMoney(totals[idx])}
                              {totals[idx] === minTotal && totals.filter(t => t !== totals[idx]).length > 0 && (
                                <Badge className="ml-2 bg-green-100 text-green-700 text-xs">En Uygun</Badge>
                              )}
                            </TableCell>
                          ))}
                        </TableRow>
                      );
                    })()}
                  </TableBody>
                </Table>
              </div>
            );
          })()}

          <DialogFooter>
            <Button variant="outline" onClick={() => setShowCompareModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teklif Ekle/Düzenle Modal */}
      <Dialog open={showAddModal} onOpenChange={setShowAddModal}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-600" />
              {isEditing ? 'Teklifi Düzenle' : 'Yeni Teklif Ekle'}
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-6 py-4">
            {/* Teklif No */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Hash className="h-4 w-4 text-orange-600" />
                Teklif No
              </Label>
              <Input
                value={isEditing && selectedOffer ? selectedOffer.offer_no : (nextOfferNo || 'Yükleniyor...')}
                disabled
                className="bg-gray-100 font-mono text-orange-600 font-semibold"
                data-testid="offer-no-input"
              />
              <p className="text-xs text-gray-500">
                {isEditing ? 'Mevcut teklif numarası' : 'Teklif numarası kayıt sırasında otomatik olarak atanır (TEK-0001, TEK-0002, ...)'}
              </p>
            </div>

            {/* İş Adı */}
            <div className="space-y-2">
              <Label className="flex items-center gap-2">
                <Briefcase className="h-4 w-4 text-orange-600" />
                İş Adı
              </Label>
              <Input
                list="offer-job-names-list"
                value={formData.job_name}
                onChange={(e) => setFormData(prev => ({ ...prev, job_name: e.target.value }))}
                placeholder="İş listesinden seçin veya elle yazın"
                data-testid="offer-job-name-input"
              />
              <datalist id="offer-job-names-list">
                {jobNames.map((n, i) => (
                  <option key={i} value={n} />
                ))}
              </datalist>
              <p className="text-xs text-gray-500">Bu teklif hangi iş için alındıysa o işin adı. Devam eden işlerden seçebilir veya elle yazabilirsiniz.</p>
            </div>

            {/* Tarih */}
            <div className="space-y-2">
              <Label>Teklif Tarihi</Label>
              <Input
                type="date"
                value={formData.offer_date}
                onChange={(e) => setFormData(prev => ({ ...prev, offer_date: e.target.value }))}
                data-testid="offer-date-input"
              />
            </div>
            
            {/* Para Birimi */}
            <div className="space-y-2">
              <Label>Para Birimi</Label>
              <Select
                value={formData.currency}
                onValueChange={(value) => setFormData(prev => ({ ...prev, currency: value }))}
              >
                <SelectTrigger data-testid="currency-select">
                  <SelectValue placeholder="Para birimi seçin..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="TL">₺ Türk Lirası (TL)</SelectItem>
                  <SelectItem value="USD">$ Amerikan Doları (USD)</SelectItem>
                  <SelectItem value="EUR">€ Euro (EUR)</SelectItem>
                  <SelectItem value="GBP">£ İngiliz Sterlini (GBP)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            {/* Cari Hesap */}
            <div className="space-y-2">
              <Label>Cari Hesap Adı *</Label>
              <Select
                value={formData.is_custom_cari ? 'diger' : formData.cari_hesap_id}
                onValueChange={handleCariSelect}
              >
                <SelectTrigger data-testid="cari-select">
                  <SelectValue placeholder="Cari hesap seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {cariHesaplar.map((cari) => (
                    <SelectItem key={cari.id} value={cari.id}>
                      {cari.name}
                    </SelectItem>
                  ))}
                  <SelectItem value="diger">Diğer (Elle Gir)</SelectItem>
                </SelectContent>
              </Select>
              {formData.is_custom_cari && (
                <Input
                  placeholder="Cari hesap adını girin..."
                  value={formData.custom_cari_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, custom_cari_name: e.target.value }))}
                  className="mt-2"
                  data-testid="custom-cari-input"
                />
              )}
            </div>
            
            {/* Şube Seçimi */}
            <div className="space-y-2">
              <Label>Şube *</Label>
              <Select
                value={formData.branch_id}
                onValueChange={(value) => {
                  const branch = branches.find(b => b.id === value);
                  setFormData(prev => ({ 
                    ...prev, 
                    branch_id: value,
                    branch_name: branch?.name || ''
                  }));
                }}
              >
                <SelectTrigger data-testid="branch-select">
                  <SelectValue placeholder="Şube seçin..." />
                </SelectTrigger>
                <SelectContent>
                  {branches.map((branch) => (
                    <SelectItem key={branch.id} value={branch.id}>
                      {branch.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            {/* Teklif İçeriği */}
            <div className="space-y-2">
              <Label>Teklif İçeriği *</Label>
              <Select
                value={formData.is_custom_type ? 'diger' : formData.offer_type}
                onValueChange={handleTypeSelect}
              >
                <SelectTrigger data-testid="type-select">
                  <SelectValue placeholder="Teklif içeriği seçin..." />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="urun">
                    <div className="flex items-center gap-2">
                      <Package className="h-4 w-4" />
                      Ürün
                    </div>
                  </SelectItem>
                  <SelectItem value="hizmet">
                    <div className="flex items-center gap-2">
                      <Wrench className="h-4 w-4" />
                      Hizmet
                    </div>
                  </SelectItem>
                  <SelectItem value="diger">Diğer (Elle Gir)</SelectItem>
                </SelectContent>
              </Select>
              {formData.is_custom_type && (
                <Input
                  placeholder="Teklif içeriğini girin..."
                  value={formData.custom_type_name}
                  onChange={(e) => setFormData(prev => ({ ...prev, custom_type_name: e.target.value }))}
                  className="mt-2"
                  data-testid="custom-type-input"
                />
              )}
            </div>
            
            {/* Ürün Seçimi */}
            {formData.offer_type === 'urun' && (
              <div className="space-y-4 p-4 border rounded-lg bg-gray-50">
                <Label className="text-base font-semibold">Ürünler</Label>
                
                {/* Ürün Ekleme */}
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-4">
                    <Select value={selectedProduct} onValueChange={handleProductSelect}>
                      <SelectTrigger data-testid="product-select">
                        <SelectValue placeholder="Ürün seçin..." />
                      </SelectTrigger>
                      <SelectContent>
                        {stokKartlari.map((stok) => (
                          <SelectItem key={stok.id} value={stok.id}>
                            {stok.name || stok.product_name}{stok.model ? ` - ${stok.model}` : ''}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="1"
                      value={productQuantity}
                      onChange={(e) => setProductQuantity(parseFloat(e.target.value) || 1)}
                      placeholder="Miktar"
                      data-testid="product-quantity-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      value={productUnit}
                      onChange={(e) => setProductUnit(e.target.value)}
                      placeholder="Birim"
                      disabled
                      className="bg-gray-100"
                      data-testid="product-unit-input"
                    />
                  </div>
                  <div className="col-span-3">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">{currencySymbols[formData.currency]}</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={productPrice}
                        onChange={(e) => setProductPrice(e.target.value)}
                        placeholder="Fiyat"
                        className="pl-7"
                        data-testid="product-price-input"
                      />
                    </div>
                  </div>
                  <div className="col-span-1">
                    <Button type="button" onClick={addProduct} className="w-full" data-testid="add-product-btn">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Eklenen Ürünler */}
                {formData.items.length > 0 && (
                  <div className="space-y-2">
                    {formData.items.map((item, index) => (
                      <div key={index} className="p-2 bg-white rounded border">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Package className="h-4 w-4 text-purple-500" />
                            <div>
                              <span className="font-medium">{item.product_name}</span>
                              {item.model && <p className="text-xs text-gray-500">Model: {item.model}</p>}
                            </div>
                            <Badge variant="outline">{item.quantity} {item.unit}</Badge>
                            <span className="text-green-600 font-mono">{formatMoney(item.price)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-600">
                              = {formatMoney(item.quantity * item.price * (1 + (item.kdv_orani || 0) / 100))}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeProduct(index)}
                              className="text-red-600"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1 ml-7">
                          <span className="text-xs text-gray-500">KDV:</span>
                          <span className="text-xs text-gray-400">%</span>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={item.kdv_orani ?? 20}
                            onChange={(e) => {
                              const updated = [...formData.items];
                              updated[index] = { ...updated[index], kdv_orani: parseFloat(e.target.value) || 0 };
                              setFormData(prev => ({ ...prev, items: updated }));
                            }}
                            className="w-16 h-6 text-xs px-1 py-0"
                            data-testid={`product-kdv-${index}`}
                          />
                          <span className="text-xs text-gray-500">
                            ({formatMoney(item.quantity * item.price * ((item.kdv_orani || 0) / 100))} KDV)
                          </span>
                        </div>
                      </div>
                    ))}
                    <div className="flex flex-col items-end gap-1 pt-2 border-t">
                      <span className="text-sm text-gray-500">
                        Ara Toplam: {formatMoney(formData.items.reduce((sum, item) => sum + (item.quantity * item.price), 0))}
                      </span>
                      <span className="text-sm text-gray-500">
                        KDV Toplamı: {formatMoney(formData.items.reduce((sum, item) => sum + (item.quantity * item.price * ((item.kdv_orani || 0) / 100)), 0))}
                      </span>
                      <span className="font-semibold">
                        Ürün Toplamı: {formatMoney(formData.items.reduce((sum, item) => sum + (item.quantity * item.price * (1 + (item.kdv_orani || 0) / 100)), 0))}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Hizmet Ekleme */}
            {formData.offer_type === 'hizmet' && (
              <div className="space-y-4 p-4 border rounded-lg bg-amber-50">
                <Label className="text-base font-semibold">Hizmetler</Label>
                
                {/* Hizmet Ekleme Formu */}
                <div className="grid grid-cols-12 gap-2">
                  <div className="col-span-4">
                    <Input
                      value={serviceName}
                      onChange={(e) => setServiceName(e.target.value)}
                      placeholder="Hizmet adı..."
                      data-testid="service-name-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      type="number"
                      min="1"
                      value={serviceQuantity}
                      onChange={(e) => setServiceQuantity(parseFloat(e.target.value) || 1)}
                      placeholder="Miktar"
                      data-testid="service-quantity-input"
                    />
                  </div>
                  <div className="col-span-2">
                    <Input
                      value={serviceUnit}
                      onChange={(e) => setServiceUnit(e.target.value)}
                      placeholder="Birim"
                      data-testid="service-unit-input"
                    />
                  </div>
                  <div className="col-span-3">
                    <div className="relative">
                      <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">{currencySymbols[formData.currency]}</span>
                      <Input
                        type="number"
                        min="0"
                        step="0.01"
                        value={servicePrice}
                        onChange={(e) => setServicePrice(e.target.value)}
                        placeholder="Fiyat"
                        className="pl-7"
                        data-testid="service-price-input"
                      />
                    </div>
                  </div>
                  <div className="col-span-1">
                    <Button type="button" onClick={addService} className="w-full" data-testid="add-service-btn">
                      <Plus className="h-4 w-4" />
                    </Button>
                  </div>
                </div>
                
                {/* Eklenen Hizmetler */}
                {formData.services.length > 0 && (
                  <div className="space-y-2">
                    {formData.services.map((srv, index) => (
                      <div key={index} className="p-2 bg-white rounded border">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-3">
                            <Wrench className="h-4 w-4 text-amber-500" />
                            <span className="font-medium">{srv.service_name}</span>
                            <Badge variant="outline">{srv.quantity} {srv.unit}</Badge>
                            <span className="text-green-600 font-mono">{formatMoney(srv.price)}</span>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-sm font-semibold text-gray-600">
                              = {formatMoney(srv.quantity * srv.price * (1 + (srv.kdv_orani || 0) / 100))}
                            </span>
                            <Button
                              type="button"
                              variant="ghost"
                              size="sm"
                              onClick={() => removeService(index)}
                              className="text-red-600"
                            >
                              <X className="h-4 w-4" />
                            </Button>
                          </div>
                        </div>
                        <div className="flex items-center gap-2 mt-1 ml-7">
                          <span className="text-xs text-gray-500">KDV:</span>
                          <span className="text-xs text-gray-400">%</span>
                          <Input
                            type="number"
                            min="0"
                            max="100"
                            value={srv.kdv_orani ?? 20}
                            onChange={(e) => {
                              const updated = [...formData.services];
                              updated[index] = { ...updated[index], kdv_orani: parseFloat(e.target.value) || 0 };
                              setFormData(prev => ({ ...prev, services: updated }));
                            }}
                            className="w-16 h-6 text-xs px-1 py-0"
                            data-testid={`service-kdv-${index}`}
                          />
                          <span className="text-xs text-gray-500">
                            ({formatMoney(srv.quantity * srv.price * ((srv.kdv_orani || 0) / 100))} KDV)
                          </span>
                        </div>
                      </div>
                    ))}
                    <div className="flex flex-col items-end gap-1 pt-2 border-t">
                      <span className="text-sm text-gray-500">
                        Ara Toplam: {formatMoney(formData.services.reduce((sum, srv) => sum + (srv.quantity * srv.price), 0))}
                      </span>
                      <span className="text-sm text-gray-500">
                        KDV Toplamı: {formatMoney(formData.services.reduce((sum, srv) => sum + (srv.quantity * srv.price * ((srv.kdv_orani || 0) / 100)), 0))}
                      </span>
                      <span className="font-semibold">
                        Hizmet Toplamı: {formatMoney(formData.services.reduce((sum, srv) => sum + (srv.quantity * srv.price * (1 + (srv.kdv_orani || 0) / 100)), 0))}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            )}
            
            {/* Nakliye Ücreti */}
            <div className="space-y-3 p-4 border rounded-lg bg-blue-50">
              <div className="flex items-center gap-3">
                <label className="flex items-center gap-2 cursor-pointer" data-testid="nakliye-toggle">
                  <input
                    type="checkbox"
                    checked={formData.nakliye_var}
                    onChange={(e) => setFormData(prev => ({
                      ...prev,
                      nakliye_var: e.target.checked,
                      nakliye_ucreti: e.target.checked ? prev.nakliye_ucreti : ''
                    }))}
                    className="w-4 h-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                  <Truck className="h-5 w-5 text-blue-600" />
                  <span className="text-base font-semibold text-blue-800">Nakliye Ücreti Var</span>
                </label>
              </div>
              {formData.nakliye_var && (
                <div className="flex items-center gap-2">
                  <Label className="text-sm text-blue-700 whitespace-nowrap">Nakliye Tutarı:</Label>
                  <div className="relative w-48">
                    <span className="absolute left-2 top-1/2 -translate-y-1/2 text-gray-400 text-sm font-medium">{currencySymbols[formData.currency]}</span>
                    <Input
                      type="number"
                      min="0"
                      step="0.01"
                      value={formData.nakliye_ucreti}
                      onChange={(e) => setFormData(prev => ({ ...prev, nakliye_ucreti: e.target.value }))}
                      placeholder="0.00"
                      className="pl-7"
                      data-testid="nakliye-amount-input"
                    />
                  </div>
                </div>
              )}
            </div>

            {/* Genel Toplam Özeti */}
            {(formData.items.length > 0 || formData.services.length > 0) && (
              <div className="p-4 border rounded-lg bg-gray-50">
                <div className="flex flex-col items-end gap-1">
                  <span className="text-sm text-gray-500">
                    Ara Toplam: {formatMoney(calculateSubtotal(formData.items, formData.services))}
                  </span>
                  <span className="text-sm text-gray-500">
                    KDV Toplamı: {formatMoney(calculateKdvTotal(formData.items, formData.services))}
                  </span>
                  {formData.nakliye_var && parseFloat(formData.nakliye_ucreti) > 0 && (
                    <span className="text-sm text-blue-600">
                      Nakliye: {formatMoney(parseFloat(formData.nakliye_ucreti))}
                    </span>
                  )}
                  <span className="font-bold text-lg border-t pt-1 mt-1" data-testid="grand-total">
                    Genel Toplam: {formatMoney(calculateTotal(formData.items, formData.services, formData.nakliye_var ? formData.nakliye_ucreti : 0))}
                  </span>
                </div>
              </div>
            )}

            {/* Ödeme Planı */}
            <div className="space-y-4 p-4 border rounded-lg bg-purple-50">
              <Label className="text-base font-semibold flex items-center gap-2">
                <DollarSign className="h-5 w-5 text-purple-600" />
                Ödeme Planı
              </Label>
                
                {/* Ödeme Türü */}
                <div className="space-y-2">
                  <div className="flex gap-2 mb-3">
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        payment_plan: [...prev.payment_plan, { type: 'cek', date: '', amount: '', description: '' }]
                      }))}
                      className="gap-1 border-purple-300 text-purple-700 hover:bg-purple-50"
                      data-testid="add-check-payment-offer"
                    >
                      <CreditCard className="h-4 w-4" />
                      Çek Ekle
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        payment_plan: [...prev.payment_plan, { type: 'nakit', date: '', amount: '', description: '' }]
                      }))}
                      className="gap-1 border-green-300 text-green-700 hover:bg-green-50"
                      data-testid="add-cash-payment-offer"
                    >
                      <DollarSign className="h-4 w-4" />
                      Nakit Ekle
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      onClick={() => setFormData(prev => ({
                        ...prev,
                        payment_plan: [...prev.payment_plan, { type: 'kredi_karti', date: '', amount: '', description: '' }]
                      }))}
                      className="gap-1 border-blue-300 text-blue-700 hover:bg-blue-50"
                      data-testid="add-credit-payment-offer"
                    >
                      <CreditCard className="h-4 w-4" />
                      Kredi Kartı Ekle
                    </Button>
                  </div>
                  {(formData.payment_plan || []).length > 0 ? (
                    <div className="space-y-2">
                      {(formData.payment_plan || []).map((payment, idx) => (
                        <div key={idx} className={`flex gap-2 items-center p-3 rounded-lg border ${
                          payment.type === 'cek' ? 'bg-purple-50 border-purple-200' :
                          payment.type === 'nakit' ? 'bg-green-50 border-green-200' :
                          'bg-blue-50 border-blue-200'
                        }`}>
                          <Badge variant="secondary" className={`text-xs whitespace-nowrap ${
                            payment.type === 'cek' ? 'bg-purple-100 text-purple-700' :
                            payment.type === 'nakit' ? 'bg-green-100 text-green-700' :
                            'bg-blue-100 text-blue-700'
                          }`}>
                            {payment.type === 'cek' ? 'Çek' : payment.type === 'nakit' ? 'Nakit' : 'Kredi Kartı'}
                          </Badge>
                          <div className="w-36">
                            <Input
                              type="date"
                              value={payment.date}
                              onChange={(e) => {
                                const updated = [...formData.payment_plan];
                                updated[idx] = { ...updated[idx], date: e.target.value };
                                setFormData(prev => ({ ...prev, payment_plan: updated }));
                              }}
                              className="text-sm"
                              data-testid={`offer-payment-date-${idx}`}
                            />
                          </div>
                          <div className="w-32">
                            <Input
                              type="number"
                              step="0.01"
                              placeholder="Tutar (₺)"
                              value={payment.amount}
                              onChange={(e) => {
                                const updated = [...formData.payment_plan];
                                updated[idx] = { ...updated[idx], amount: e.target.value };
                                setFormData(prev => ({ ...prev, payment_plan: updated }));
                              }}
                              className="text-sm text-right"
                              data-testid={`offer-payment-amount-${idx}`}
                            />
                          </div>
                          <div className="flex-1">
                            <Input
                              placeholder="Açıklama"
                              value={payment.description}
                              onChange={(e) => {
                                const updated = [...formData.payment_plan];
                                updated[idx] = { ...updated[idx], description: e.target.value };
                                setFormData(prev => ({ ...prev, payment_plan: updated }));
                              }}
                              className="text-sm"
                              data-testid={`offer-payment-desc-${idx}`}
                            />
                          </div>
                          <Button
                            type="button"
                            variant="ghost"
                            size="sm"
                            onClick={() => {
                              const updated = (formData.payment_plan || []).filter((_, i) => i !== idx);
                              setFormData(prev => ({ ...prev, payment_plan: updated }));
                            }}
                            className="text-red-500 hover:text-red-700"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </div>
                      ))}
                      <div className="flex justify-end pt-1">
                        <span className="text-sm font-semibold text-gray-700">
                          Toplam: {(formData.payment_plan || []).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0).toLocaleString('tr-TR')} ₺
                        </span>
                      </div>
                    </div>
                  ) : (
                    <div className="text-center py-3 text-gray-400 text-sm border border-dashed rounded-lg">
                      Ödeme planı eklenmedi
                    </div>
                  )}
                </div>
                
                {/* Peşin Bilgisi */}
                {formData.payment_type === 'pesin' && (
                  <div className="p-3 bg-green-100 rounded-lg border border-green-200">
                    <p className="text-sm text-green-700 flex items-center gap-2">
                      <CheckCircle className="h-4 w-4" />
                      Ödeme teslimatta peşin olarak yapılacaktır.
                    </p>
                  </div>
                )}
              </div>
            </div>
            
            {/* Belgeler */}
            <div className="space-y-2">
              <Label>Teklif Belgeleri</Label>
              <div className="flex items-center gap-2">
                <Input
                  type="file"
                  multiple
                  onChange={handleFileUpload}
                  className="hidden"
                  id="offer-files"
                  data-testid="file-upload-input"
                />
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => document.getElementById('offer-files').click()}
                >
                  <Upload className="h-4 w-4 mr-2" />
                  Belge Yükle
                </Button>
              </div>
              {formData.documents.length > 0 && (
                <div className="space-y-1 mt-2">
                  {formData.documents.map((doc, index) => (
                    <div key={index} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                      <a 
                        href={doc.url || doc} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-blue-600 hover:underline text-sm truncate flex-1"
                      >
                        {doc.filename || doc.name || `Belge ${index + 1}`}
                      </a>
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        onClick={() => removeDocument(index)}
                        className="text-red-600"
                      >
                        <X className="h-4 w-4" />
                      </Button>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Notlar */}
            <div className="space-y-2">
              <Label>Notlar</Label>
              <Textarea
                placeholder="Ek notlar..."
                value={formData.notes}
                onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                rows={2}
                data-testid="notes-input"
              />
            </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => { setShowAddModal(false); resetForm(); setIsEditing(false); }}>
              İptal
            </Button>
            <Button onClick={handleSaveOffer} className="bg-orange-600 hover:bg-orange-700" data-testid="save-offer-btn">
              {isEditing ? 'Güncelle' : 'Kaydet'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Teklif Görüntüle Modal */}
      <Dialog open={showViewModal} onOpenChange={setShowViewModal}>
        <DialogContent className="max-w-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <FileText className="h-5 w-5 text-orange-600" />
              Teklif Detayı
              {selectedOffer?.offer_no && (
                <Badge variant="outline" className="ml-2 font-mono">{selectedOffer.offer_no}</Badge>
              )}
            </DialogTitle>
          </DialogHeader>
          
          {selectedOffer && (
            <div className="space-y-4 py-4">
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className="text-gray-500">Tarih</Label>
                  <p className="font-medium">{formatDate(selectedOffer.offer_date)}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Durum</Label>
                  <div>{getStatusBadge(selectedOffer.status)}</div>
                </div>
                <div>
                  <Label className="text-gray-500">Cari Hesap</Label>
                  <p className="font-medium">{selectedOffer.cari_hesap_name}</p>
                </div>
                <div>
                  <Label className="text-gray-500">Teklif İçeriği</Label>
                  <div>{getTypeBadge(selectedOffer)}</div>
                </div>
              </div>
              
              {selectedOffer.offer_type === 'urun' && selectedOffer.items?.length > 0 && (
                <div>
                  <Label className="text-gray-500">Ürünler</Label>
                  <div className="mt-2 space-y-1">
                    {selectedOffer.items.map((item, idx) => (
                      <div key={idx} className="p-2 bg-gray-50 rounded">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Package className="h-4 w-4 text-purple-500" />
                            <div>
                              <span>{item.product_name}</span>
                              {item.model && <p className="text-xs text-gray-500">Model: {item.model}</p>}
                            </div>
                            <Badge variant="outline">{item.quantity} {item.unit}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-green-600">{formatMoney(item.price)}</span>
                            <span className="font-semibold">= {formatMoney(item.quantity * item.price * (1 + (item.kdv_orani || 0) / 100))}</span>
                          </div>
                        </div>
                        {item.kdv_orani > 0 && (
                          <div className="text-xs text-gray-400 ml-6">KDV %{item.kdv_orani} ({formatMoney(item.quantity * item.price * (item.kdv_orani / 100))})</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedOffer.offer_type === 'hizmet' && selectedOffer.services?.length > 0 && (
                <div>
                  <Label className="text-gray-500">Hizmetler</Label>
                  <div className="mt-2 space-y-1">
                    {selectedOffer.services.map((srv, idx) => (
                      <div key={idx} className="p-2 bg-gray-50 rounded">
                        <div className="flex items-center justify-between">
                          <div className="flex items-center gap-2">
                            <Wrench className="h-4 w-4 text-amber-500" />
                            <span>{srv.service_name}</span>
                            <Badge variant="outline">{srv.quantity} {srv.unit}</Badge>
                          </div>
                          <div className="flex items-center gap-2">
                            <span className="text-green-600">{formatMoney(srv.price)}</span>
                            <span className="font-semibold">= {formatMoney(srv.quantity * srv.price * (1 + (srv.kdv_orani || 0) / 100))}</span>
                          </div>
                        </div>
                        {srv.kdv_orani > 0 && (
                          <div className="text-xs text-gray-400 ml-6">KDV %{srv.kdv_orani} ({formatMoney(srv.quantity * srv.price * (srv.kdv_orani / 100))})</div>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Toplam Özet */}
              {(selectedOffer.items?.length > 0 || selectedOffer.services?.length > 0) && (
                <div className="p-3 bg-gray-100 rounded-lg">
                  <div className="flex flex-col items-end gap-1">
                    <span className="text-sm text-gray-500">Ara Toplam: {formatMoney(calculateSubtotal(selectedOffer.items, selectedOffer.services))}</span>
                    {calculateKdvTotal(selectedOffer.items, selectedOffer.services) > 0 && (
                      <span className="text-sm text-gray-500">KDV Toplamı: {formatMoney(calculateKdvTotal(selectedOffer.items, selectedOffer.services))}</span>
                    )}
                    {selectedOffer.nakliye_var && parseFloat(selectedOffer.nakliye_ucreti) > 0 && (
                      <span className="text-sm text-blue-600 flex items-center gap-1">
                        <Truck className="h-3 w-3" /> Nakliye: {formatMoney(parseFloat(selectedOffer.nakliye_ucreti))}
                      </span>
                    )}
                    <span className="font-bold text-lg border-t pt-1 mt-1">
                      Toplam: {formatMoney(calculateTotal(selectedOffer.items, selectedOffer.services, selectedOffer.nakliye_var ? selectedOffer.nakliye_ucreti : 0))}
                    </span>
                  </div>
                </div>
              )}
              
              {selectedOffer.documents?.length > 0 && (
                <div>
                  <Label className="text-gray-500">Belgeler</Label>
                  <div className="mt-2 space-y-1">
                    {selectedOffer.documents.map((doc, idx) => (
                      <a 
                        key={idx}
                        href={doc.url || doc}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-2 p-2 bg-gray-50 rounded text-blue-600 hover:underline"
                      >
                        <FileText className="h-4 w-4" />
                        {doc.filename || doc.name || `Belge ${idx + 1}`}
                      </a>
                    ))}
                  </div>
                </div>
              )}
              
              {selectedOffer.notes && (
                <div>
                  <Label className="text-gray-500">Notlar</Label>
                  <p className="mt-1 p-2 bg-gray-50 rounded">{selectedOffer.notes}</p>
                </div>
              )}
              
              <div className="text-sm text-gray-500">
                Oluşturan: {selectedOffer.created_by_name} | {formatDate(selectedOffer.created_at)}
              </div>
              
              {/* Durum Değiştirme */}
              {selectedOffer.status === 'aktif' && (
                <div className="flex gap-2 pt-4 border-t">
                  <Button
                    onClick={() => updateStatus(selectedOffer.id, 'kabul_edildi')}
                    className="bg-green-600 hover:bg-green-700"
                  >
                    <CheckCircle className="h-4 w-4 mr-2" />
                    Kabul Et
                  </Button>
                  <Button
                    onClick={() => updateStatus(selectedOffer.id, 'reddedildi')}
                    variant="destructive"
                  >
                    <XCircle className="h-4 w-4 mr-2" />
                    Reddet
                  </Button>
                </div>
              )}
            </div>
          )}
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowViewModal(false)}>
              Kapat
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PurchasingOffers;
